/**
 * Signature Section Component
 * ===========================
 * 
 * Wrapper component that manages signature input and display for form fields.
 * This component provides the interface between individual form components and
 * the signature capture functionality, handling both signature creation and display.
 * 
 * Purpose:
 * - Renders signature input interface when no signature exists
 * - Displays saved signature preview when signature is present
 * - Manages signature lifecycle (create, view, clear)
 * - Integrates with SignatureService for data persistence
 * 
 * Usage:
 * - Rendered in any form that has a signature field requirement
 * - Receives signature requirements from parent form components
 * - Automatically handles signature state changes
 * - Provides consistent signature UI across all forms
 * 
 * Component Architecture:
 * - Wrapper component that conditionally renders SignaturePadComponent
 * - Manages signature display and interaction logic
 * - Integrates with form validation and submission workflows
 * 
 * Theme Support:
 * - Automatic dark/light mode detection
 * - Responsive to system theme changes
 * - Ionic theme class support for manual theme switching
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { SignatureService } from '../../../../services/signature.service';
import { SignatureInfo } from '../../../../models/forms/attachments/form-attachment-requirements';

@Component({
  selector: 'app-signature-section',
  template: `
    <!-- 
      SIGNATURE FIELD LABEL
      ======================
      Displays the signature field label from form requirements.
      Label is dynamically set based on signatureInfo input.
    -->
    <ion-label position="floating" class="form-label">
      <strong>{{ signatureInfo.label }}:</strong> <ion-text *ngIf="signatureInfo.required" color="danger">*</ion-text>
    </ion-label>
    
    <!-- 
      SIGNATURE INPUT/PRESENTATION ROW
      =================================
      Responsive row layout for signature components.
      Uses 12 columns on mobile, 4 columns on medium+ screens.
    -->
    <ion-row>
      <ion-col size="12" size-md="4">
        
        <!-- 
          SIGNATURE PAD COMPONENT
          =========================
          Renders signature capture interface when no signature exists.
          Only shown when hasSignature is false.
          Emits signatureSaved event when signature is captured.
        -->
        <app-signature-pad
          *ngIf="!hasSignature"
          [identifier]="signatureInfo.identifier"
          (signatureSaved)="onSignatureSaved($event)">
        </app-signature-pad>

        <!-- 
          SIGNATURE PREVIEW
          ==================
          Displays saved signature image when signature exists.
          Shows signature preview with clear button for management.
          Only shown when hasSignature is true.
        -->
        <div *ngIf="hasSignature" class="signature-preview">
          <!-- 
            SIGNATURE IMAGE
            ================
            Displays the captured signature as an image.
            Uses base64 data URL from signature service.
            Responsive sizing with max dimensions.
          -->
          <img 
            [src]="getSignatureDataUrl()" 
            alt="Signature" />
          
          <!-- 
            CLEAR SIGNATURE BUTTON
            ========================
            Allows user to remove existing signature.
            Triggers clearSignature() method to reset signature state.
            Clear button styling for clear action indication.
          -->
          <ion-button fill="clear" (click)="clearSignature()">Clear Signature</ion-button>
        </div>
        
      </ion-col>
    </ion-row>
  `,
  styles: [`
    /* 
      FORM CARD STYLING
      ==================
      Consistent styling for signature form sections.
      Matches overall form design patterns.
    */
    .form-card {
      margin-bottom: 24px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    /* 
      SIGNATURE PREVIEW CONTAINER
      ============================
      Styling for the signature preview display area.
      Centers content and provides proper spacing.
    */
    .signature-preview {
      margin-top: 16px;
      text-align: center;
    }
    
    /* 
      SIGNATURE IMAGE STYLING
      ========================
      Responsive image sizing with consistent borders.
      Ensures signatures display properly across devices.
    */
    .signature-preview img {
      max-width: 100%;
      max-height: 100px;
      border: 1px solid #ccc;
      border-radius: 4px;
      margin-bottom: 8px;
    }
    
    /* 
      DARK MODE SUPPORT - SYSTEM PREFERENCE
      ======================================
      Automatic dark mode styling based on system preference.
      Adjusts border colors for better visibility in dark themes.
    */
    @media (prefers-color-scheme: dark) {
      .signature-preview img {
        border-color: #666;
      }
    }
    
    /* 
      DARK MODE SUPPORT - IONIC THEME CLASS
      ======================================
      Manual dark mode support for Ionic theme switching.
      Ensures consistent appearance when theme is manually toggled.
    */
    :host-context(.dark) .signature-preview img {
      border-color: #666;
    }
  `]
})
export class SignatureSectionComponent implements OnInit, OnDestroy {
  
  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================
  // These properties define the signature requirements and configuration
  
  /** 
   * Signature information defining the signature field requirements.
   * Contains identifier, label, and validation rules for the signature field.
   * Provided by parent form component based on form metadata.
   */
  @Input() signatureInfo!: SignatureInfo;
  
  // ============================================================================
  // PRIVATE PROPERTIES
  // ============================================================================
  // Internal state management for theme detection and component lifecycle
  
  /** 
   * Cached dark mode state for performance optimization.
   * Updated when theme changes are detected.
   */
  private isDarkModeValue = false;
  
  /** 
   * Media query listener for system theme preference changes.
   * Monitors system dark/light mode preference changes.
   */
  private mediaQuery: MediaQueryList | null = null;
  
  /** 
   * Mutation observer for Ionic theme class changes.
   * Monitors document body class changes for manual theme switching.
   */
  private observer: MutationObserver | null = null;
  
  constructor(private signatureService: SignatureService) {}

  // ============================================================================
  // LIFECYCLE METHODS
  // ============================================================================
  // Component initialization and cleanup
  
  /**
   * Lifecycle hook that initializes the component.
   * Sets up theme detection listeners for responsive appearance.
   * Called automatically by Angular after component construction.
   */
  ngOnInit() {
    this.setupThemeListener();
  }

  /**
   * Lifecycle hook that cleans up the component.
   * Removes event listeners and observers to prevent memory leaks.
   * Called automatically by Angular before component destruction.
   */
  ngOnDestroy() {
    // Clean up media query listener
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleThemeChange.bind(this));
    }
    
    // Clean up mutation observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // ============================================================================
  // THEME DETECTION METHODS
  // ============================================================================
  // Methods for detecting and responding to theme changes
  
  /**
   * Sets up listeners for theme changes.
   * Monitors both system preference changes and Ionic theme class changes.
   * Ensures signature display adapts to current theme automatically.
   */
  private setupThemeListener() {
    // Listen for system theme changes
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener('change', this.handleThemeChange.bind(this));
    
    // Listen for Ionic theme changes
    this.observer = new MutationObserver(() => {
      this.handleThemeChange();
    });
    
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Perform initial theme check
    this.handleThemeChange();
  }

  /**
   * Handles theme change events.
   * Updates cached dark mode state when theme changes are detected.
   * Called by both system preference and Ionic theme change listeners.
   */
  private handleThemeChange() {
    this.isDarkModeValue = this.checkDarkMode();
  }

  /**
   * Checks if dark mode is currently active.
   * Prioritizes Ionic theme class over system preference.
   * Ensures consistent theme detection across different switching methods.
   * 
   * @returns true if dark mode is active, false otherwise
   */
  private checkDarkMode(): boolean {
    // Check for Ionic dark class (manual theme switching)
    if (document.body.classList.contains('dark')) {
      return true;
    }
    
    // Check for system preference (automatic theme switching)
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Public method to check current dark mode state.
   * Provides access to cached dark mode value for external components.
   * 
   * @returns true if dark mode is active, false otherwise
   */
  public isDarkMode(): boolean {
    return this.isDarkModeValue;
  }

  // ============================================================================
  // SIGNATURE STATE GETTERS
  // ============================================================================
  // Computed properties for signature state and data
  
  /**
   * Checks if a signature exists for the current identifier.
   * Used to conditionally render signature pad vs signature preview.
   * 
   * @returns true if signature exists, false otherwise
   */
  get hasSignature(): boolean {
    return this.signatureService.hasSignature(this.signatureInfo.identifier);
  }

  /**
   * Gets the signature data for the current identifier.
   * Provides access to signature metadata and base64 data.
   * 
   * @returns Signature data object or undefined if no signature exists
   */
  get signatureData() {
    return this.signatureService.getSignature(this.signatureInfo.identifier);
  }

  /**
   * Gets the data URL for displaying the signature image.
   * Converts base64 data to displayable image URL.
   * Handles theme-specific image processing for optimal display.
   * 
   * @returns Data URL string for signature image display
   */
  getSignatureDataUrl(): string {
    const signatureData = this.signatureData;
    if (signatureData?.base64Data) {
      // Always return the original signature data
      // The inversion will be handled by CSS filter in dark mode
      return `data:image/png;base64,${signatureData.base64Data}`;
    }
    return '';
  }

  // ============================================================================
  // SIGNATURE EVENT HANDLERS
  // ============================================================================
  // Methods for handling signature-related user interactions
  
  /**
   * Handles signature saved events from the signature pad component.
   * Currently no additional processing needed as signature is already saved in service.
   * 
   * @param base64Data - The base64 encoded signature data
   */
  onSignatureSaved(base64Data: string) {
    // Signature is already saved in the service by SignaturePadComponent
    // No additional processing needed in this wrapper component
  }

  /**
   * Clears the signature for the current identifier.
   * Removes signature data from the service and updates display.
   * Called when user clicks the clear signature button.
   */
  clearSignature() {
    this.signatureService.removeSignature(this.signatureInfo.identifier);
  }
} 